import { VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import Button from "@pathwright/ui/src/components/button/Button"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import PropTypes from "prop-types"
import { useState } from "react"
import CurrencyFormatterProvider, {
  useCurrencyFormatter
} from "../../currency/CurrencyFormatterProvider"
import ConditionalOrderDiscountForm from "../../order/ConditionalOrderDiscountForm"
import {
  LICENSING_MODES,
  LICENSING_MODE_GROUP_SUBSCRIPTION,
  LICENSING_MODE_PRIVATE_COHORTS,
  LICENSING_MODE_SCHOOL_LICENSING
} from "../../store/resource/offering/license/constants"
import LicenseResourceGroupSubscriptionForm from "./LicenseResourceGroupSubscriptionForm"
import LicenseResourceGroupSubscriptionGate from "./LicenseResourceGroupSubscriptionGate"
import LicenseResourcePrivateCohortsForm from "./LicenseResourcePrivateCohortsForm"
import LicenseResourcePrivateCohortsGate from "./LicenseResourcePrivateCohortsGate"
import LicenseResourceSchoolLicensingForm from "./LicenseResourceSchoolLicensingForm"
import LicenseGroupName from "./shared/LicenseGroupName"
import LicenseSeatSlider from "./shared/LicenseSeatSlider"

const DefaultGateComponent = ({ children }) => children
// get's an optional gate component that will render in place
// of corresponding form component. GateComponent is expected
// to render its children if no gating is required
const GateComponent = (props) => {
  const { licenseOffering } = props
  const licensingModes = LICENSING_MODES._for(licenseOffering.licensing_mode)

  switch (licenseOffering.licensing_mode) {
    case licensingModes.PRIVATE_COHORTS:
      return <LicenseResourcePrivateCohortsGate {...props} />
    case licensingModes.SCHOOL_LICENSING:
      return <DefaultGateComponent {...props} />
    case licensingModes.GROUP_SUBSCRIPTION:
      return <LicenseResourceGroupSubscriptionGate {...props} />
  }
}

const LicenseResourceForm = ({
  licenseOffering,
  licensorSchool,
  licenseeSchool,
  computedDiscount,
  discountCode,
  applyDiscountCode,
  clearDiscountCode,
  seatCount,
  setSeatCount,
  canGoBack,
  onBack,
  onSuccess,
  refetchLicenseOffering,
  redirectTo,
  paymentType
}) => {
  const [error, setError] = useState(null)
  const [cohortName, setCohortName] = useState("")
  // for non-debounced state setting (setSeatCount is debounced)
  const [seats, setSeats] = useState(seatCount)

  const { t } = useTranslate()
  const { currencyFormatter } = useCurrencyFormatter()

  const basePrice = seatCount * Math.max(0, licenseOffering.cost_per_seat)

  const totalPrice = computedDiscount
    ? computedDiscount.discounted_total
    : basePrice

  // const shouldProcessPayment =
  // seatCount > 0 && licenseOffering.cost_per_seat > 0 && totalPaymentPrice > 0

  return (
    <CurrencyFormatterProvider school={licensorSchool}>
      <VStack gap={4} w="full" className="LicenseResourceForm">
        {canGoBack && (
          <Button styleType="blank" style={{ paddingLeft: 0 }} onClick={onBack}>
            <Pathicon icon="arrow-left" />
            {t("resource.licensing_form.back")}
          </Button>
        )}
        {!!error && <Alert error={error} onClear={() => setError(null)} />}
        <GateComponent
          licenseOffering={licenseOffering}
          refetchLicenseOffering={refetchLicenseOffering}
          redirectTo={redirectTo}
          paymentType={paymentType}
        >
          {licensorSchool.id === licenseeSchool.id && (
            <LicenseGroupName
              value={cohortName}
              onChange={(cohortName) => setCohortName(cohortName)}
              // NOTE: this is highlighting the field before the user has interacted. We need to use formik to handle touched state
              // and only show error after the user has interacted. We'll hide for now as it's at least better UX than showing an error before the user has interacted.
              // errors={!cohortName ? ["Required"] : []}
              errors={[]}
            />
          )}
          {licenseOffering.cost_per_seat > 0 && (
            <LicenseSeatSlider
              initialValue={seatCount}
              licenseOffering={licenseOffering}
              licenseeSchool={licenseeSchool}
              licensingType="school"
              value={seats}
              computedDiscount={computedDiscount}
              onChange={(seats) => {
                setSeats(seats)
                setSeatCount(seats) // update parent form (debounced)
              }}
              currencyFormatter={currencyFormatter}
            />
          )}
          {(totalPrice > 0 || !!computedDiscount) && (
            <ConditionalOrderDiscountForm
              resourceId={licenseOffering.resource.id}
              minSeats={1}
              applyDiscountCode={applyDiscountCode}
              clearDiscountCode={clearDiscountCode}
            />
          )}
          {licenseOffering.licensing_mode === LICENSING_MODE_PRIVATE_COHORTS ? (
            <LicenseResourcePrivateCohortsForm
              licenseOffering={licenseOffering}
              licensorSchool={licensorSchool}
              price={totalPrice}
              discountCode={discountCode}
              seatCount={seatCount}
              cohortName={cohortName}
            />
          ) : licenseOffering.licensing_mode ===
            LICENSING_MODE_SCHOOL_LICENSING ? (
            <LicenseResourceSchoolLicensingForm
              licenseOffering={licenseOffering}
              licensorSchool={licensorSchool}
              licenseeSchool={licenseeSchool}
              price={totalPrice}
              discountCode={discountCode}
              seatCount={seatCount}
            />
          ) : licenseOffering.licensing_mode ===
            LICENSING_MODE_GROUP_SUBSCRIPTION ? (
            <LicenseResourceGroupSubscriptionForm
              licenseOffering={licenseOffering}
              cohortName={cohortName}
              onSuccess={onSuccess}
              onError={setError}
            />
          ) : null}
        </GateComponent>
      </VStack>
    </CurrencyFormatterProvider>
  )
}

LicenseResourceForm.displayName = "LicenseResourceForm"

LicenseResourceForm.propTypes = {
  licenseOffering: PropTypes.object.isRequired,
  licensorSchool: PropTypes.object.isRequired,
  licenseeSchool: PropTypes.object.isRequired,
  applyDiscountCode: PropTypes.func.isRequired,
  clearDiscountCode: PropTypes.func.isRequired,
  setSeatCount: PropTypes.func.isRequired,
  computedDiscount: PropTypes.object,
  seatCount: PropTypes.number,
  // Cover Offer CTA: free | one_off | subscription. Omit for ResourceAbout.
  paymentType: PropTypes.string
}

export default LicenseResourceForm
